import React, { useCallback, useState } from "react";
import {
  Avatar,
  Button,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  TextField,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { Help, LockOutlined } from "@material-ui/icons";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink, Redirect, useHistory } from "react-router-dom";
import { useAppDispatch, useTypedSelector } from "../../app/store";
import { cancelTwoFactor, contractLogin2fa } from "./loginSlice";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      marginTop: theme.spacing(8),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.secondary.main,
    },
    form: {
      width: "100%",
      marginTop: theme.spacing(1),
    },
    help: {
      padding: 2,
      marginLeft: theme.spacing(0.3),
      verticalAlign: "middle",
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    recoveryLink: {
      display: "block",
      alignSelf: "stretch",
      marginTop: theme.spacing(2),
      textAlign: "left",
      textDecoration: "underline",
    },
    helpDialog: {
      maxWidth: 430,
    },
  })
);

interface TwoFactorFormValues {
  authenticationCode: string;
}

export function TwoFactorPage() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const state = useTypedSelector((x) => x.login);
  const [helpOpen, setHelpOpen] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<TwoFactorFormValues>({
    mode: "all",
    defaultValues: { authenticationCode: "" },
  });

  const onSubmit = useCallback(
    async (values: TwoFactorFormValues) => {
      await dispatch(contractLogin2fa(values));
    },
    [dispatch]
  );

  const onCancel = useCallback(() => {
    dispatch(cancelTwoFactor());
    history.replace("/login");
  }, [dispatch, history]);

  if (state.loggedIn) {
    return <Redirect to="/contract" />;
  }
  if (!state.pendingAccessToken || !state.requiresTwoFactor) {
    return <Redirect to="/login" />;
  }

  return (
    <>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <LockOutlined />
          </Avatar>
          <Typography component="h1" variant="h5">
            二要素認証
          </Typography>
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography component="p" variant="body1">
              認証アプリに表示されたワンタイムパスワード（6桁）を入力してください。
              <IconButton
                className={classes.help}
                aria-label="二要素認証について"
                onClick={() => setHelpOpen(true)}
              >
                <Help color="primary" fontSize="small" />
              </IconButton>
            </Typography>
            <Controller
              control={control}
              name="authenticationCode"
              rules={{
                required: "ワンタイムパスワードを入力してください",
              }}
              render={({ field }) => {
                return <TextField
                  {...field}
                  label="ワンタイムパスワード"
                  variant="outlined"
                  margin="normal"
                  inputProps={{ maxLength: 6, inputMode: "numeric" }}
                  error={Boolean(errors.authenticationCode)}
                  helperText={errors.authenticationCode?.message}
                  fullWidth
                  autoFocus
                />
              }}
            />
            <Button
              className={classes.submit}
              color="primary"
              variant="contained"
              type="submit"
              disabled={!isValid || isSubmitting}
              fullWidth
            >
              ログイン
            </Button>
            <Button
              variant="contained"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onCancel}
              fullWidth
            >
              キャンセル
            </Button>
          </form>

          <MuiLink
            className={classes.recoveryLink}
            component={RouterLink}
            variant="body2"
            to="/login/recovery-code"
          >
            認証アプリを使用できない場合
          </MuiLink>
        </div>
      </Container>

      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        classes={{ paper: classes.helpDialog }}
      >
        <DialogTitle>二要素認証について</DialogTitle>
        <DialogContent>
          二要素認証設定時に使用した認証アプリに表示されたワンタイムパスワードを入力してください。認証アプリはMicrosoft Authenticator（推奨）とGoogle Authenticatorをサポートしています。
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
